import { defineStore } from 'pinia';
import AiSettingsService from '../services/AiSettingsService';

export const useAiStore = defineStore('plugin:ai', () =>
{
    const aiSettings = ref<AiOptions>(defaultAiSettings());
    const settingsState = ref<'loading' | 'ready' | 'error'>('loading');

    function hasFeatures(...features: AIFEATURE[])
    {
        return features.every(feature => aiSettings.value.features[feature] != null);
    }

    const aiEnabled = computed(() => hasFeatures('text'));
    const enablePictureAiMetadata = computed(() => hasFeatures('vision'));

    const aiProviders = computed(() => aiSettings.value.providers);
    const aiProviderOptions = computed<Option[]>(() =>
        aiProviders.value.map(config => ({
            value: config.id,
            text: config.name
        }))
    );

    const aiModels = computed(() => aiSettings.value.models);
    const aiModelOptions = computed(() => (type?: AIFEATURE) => aiModels.value
        .filter(config => !type || config.tags.includes(type))
        .map(config => ({
            value: config.id,
            text: config.name
        }))
    );

    const getDefaultModelByType = computed(() => (type: AIFEATURE) =>
    {
        if (!aiModels.value) return undefined;

        const modelId = aiSettings.value.features[type];
        const defaultModel = aiModels.value.find(m => m.id === modelId);

        if (defaultModel)
            return defaultModel;

        return null;
    });

    async function loadAiSettings()
    {
        settingsState.value = 'loading';
        try
        {
            aiSettings.value = await AiSettingsService.getSettings();
            settingsState.value = 'ready';
        }
        catch (error)
        {
            settingsState.value = 'error';
            console.error(error);
        }
    }

    async function reload()
    {
        await loadAiSettings();
    }

    reload();

    return { aiSettings, settingsState, aiEnabled, enablePictureAiMetadata, hasFeatures, aiModelOptions, aiProviderOptions, getDefaultModelByType, reload };
});
